import {
  DefaultModalDialog,
  InputField,
  SelectField,
} from '~/common/components/defaultUIComponents';
import {
  StampType,
  TimestampDate,
} from '~/common/entities/Employees/EmployeeTypes';
import WorkingInfoApi from '~/common/api/zeiterfassungServerApi/WorkingInfoApi';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { Box, Flex } from '@chakra-ui/react';
import * as dateFns from 'date-fns';
import * as yup from 'yup';
import React from 'react';

type Props = {
  employee: Employee;
  date: TimestampDate;
  isOpen: boolean;
  onToggle: () => void;
};

export const CreateTimestampModal: React.FC<Props> = ({
  employee,
  date,
  isOpen,
  onToggle,
}) => {
  const { mutateAsync: insertTimestamp } =
    WorkingInfoApi.hooks.useInsertTimestamp(employee);
  function test(this: yup.TestContext<Record<string, unknown>>): boolean {
    const values = this.parent as Record<string, string>;
    const [startHour, startMin] = values?.start?.split(':') ?? [null, null];
    const [endHour, endMin] = values?.end?.split(':') ?? [null, null];
    if (!startHour || !endHour) {
      return false;
    }
    const editedStart = dateFns.set(date.date as unknown as number, {
      minutes: parseInt(startMin as unknown as string),
      hours: parseInt(startHour),
    });
    const editedEnd = dateFns.set(date.date as unknown as number, {
      minutes: parseInt(endMin as unknown as string),
      hours: parseInt(endHour),
    });
    return dateFns.isBefore(editedStart, editedEnd);
  }
  function testEncapsulingWork(
    this: yup.TestContext<Record<string, unknown>>,
  ): boolean {
    const values = this.parent as Record<string, string>;
    const [startHour, startMin] = values?.start?.split(':') ?? [null, null];
    const [endHour, endMin] = values?.end?.split(':') ?? [null, null];
    if (!startHour || !endHour) {
      return false;
    }
    const editedStart = dateFns.set(date.date as unknown as number, {
      minutes: parseInt(startMin as unknown as string),
      hours: parseInt(startHour),
    });
    const editedEnd = dateFns.set(date.date as unknown as number, {
      minutes: parseInt(endMin as unknown as string),
      hours: parseInt(endHour),
    });
    if (values?.type === StampType.PAUSE) {
      const encapsulingWork = date.timestamps?.find(
        (ts) =>
          dateFns.isBefore(ts.start, editedStart) &&
          ts.end &&
          dateFns.isAfter(ts.end, editedEnd) &&
          ts.type === StampType.WORK,
      );
      return !!encapsulingWork;
    }
    return true;
  }
  return (
    <DefaultModalDialog
      dialogHeaderText="createTimestamp.header"
      headerParams={[
        dateFns.format(date.date as unknown as number, 'dd.MM.yyyy'),
      ]}
      submitText="createTimestamp.submit"
      isOpen={isOpen}
      showCancelButton
      onCancel={onToggle}
      onSubmit={({
        start,
        end,
        type,
      }: {
        start: string;
        end: string;
        type: StampType;
      }) =>
        insertTimestamp({ date, start, end, stampType: type }).then(onToggle)
      }
      initialValues={{
        start: '00:00',
        end: '00:00',
        type: StampType.INITIAL,
      }}
      size="2xl"
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={yup.object().shape({
        start: yup
          .string()
          .required()
          .test({ test, message: 'createTimestamp.startbeforeend' })
          .test({
            test: testEncapsulingWork,
            message: 'createTimestamp.pauseOnlyWithinWork',
          }),
        end: yup.string().required(),
        type: yup
          .string()
          .required('createTimestamp.type.yup')
          .notOneOf([StampType.INITIAL], 'createTimestamp.type.yup'),
      })}
    >
      <Flex direction="row">
        <Box w="full" pr={1}>
          <InputField label="createTimestamp.start" name="start" type="time" />
        </Box>
        <Box w="full" pl={1} pr={1}>
          <InputField label="createTimestamp.end" name="end" type="time" />
        </Box>
        <Box pl={1}>
          <SelectField
            name="type"
            fields={[StampType.INITIAL, StampType.WORK, StampType.PAUSE]}
            labels={(field) => {
              switch (field) {
                case StampType.WORK:
                  return 'createTimestamp.work';
                case StampType.PAUSE:
                  return 'createTimestamp.pause';
                default:
                  return 'createTimestamp.initial';
              }
            }}
            values={(field) => field}
            keys={(field) => field}
            label="createTimestamp.type"
          />
        </Box>
      </Flex>
    </DefaultModalDialog>
  );
};
